import { formatHumanName, isUUID } from "@medplum/core";
import type { Patient } from "@medplum/fhirtypes";
import { getPatientMemberListPhoneTelecomValues } from "@benchmark/core/utils";

/**
 * Maximum number of patient spotlight results.
 * @agentic connections
 */
const MAX_RESULTS = 5;

/** GraphQL field selection shared by PatientList branches in spotlight search. @agentic connections */
const PATIENT_SPOTLIGHT_FIELDS = `
  resourceType
  id
  identifier { system value }
  name { given family use }
  birthDate
  telecom { system value }
`;

/**
 * GraphQL response payload for patient spotlight search.
 * @agentic connections
 */
export type PatientSearchGraphQLResponse = {
  readonly data: Readonly<Record<string, readonly Patient[] | undefined>>;
};

/**
 * Builds a Medplum GraphQL query for patient search parity.
 * Uses UUID for `_id`, phone-like input for repeated `telecom` OR branches, else `name`/`identifier`.
 * @agentic connections
 */
export const buildPatientSearchGraphQLQuery = (input: string): string => {
  const escaped = JSON.stringify(input);
  const fieldsOneLine = PATIENT_SPOTLIGHT_FIELDS.replace(/\s+/g, " ").trim();

  if (isUUID(input)) {
    return `{
      Patients1: PatientList(_id: ${escaped}, _count: 1) {
        ${fieldsOneLine}
      }
    }`.replaceAll(/\s+/g, " ");
  }

  const phoneTelecomValues = getPatientMemberListPhoneTelecomValues(input);
  if (phoneTelecomValues && phoneTelecomValues.length > 0) {
    const branches = phoneTelecomValues.map(
      (value, index) =>
        `PatientsPhone${index}: PatientList(telecom: ${JSON.stringify(value)}, _count: 5) { ${fieldsOneLine} }`,
    );
    return `{ ${branches.join(" ")} }`.replaceAll(/\s+/g, " ");
  }

  return `{
    Patients1: PatientList(name: ${escaped}, _count: 5) {
      ${fieldsOneLine}
    }
    Patients2: PatientList(identifier: ${escaped}, _count: 5) {
      ${fieldsOneLine}
    }
  }`.replaceAll(/\s+/g, " ");
};

/**
 * Returns de-duped, relevance-ranked patient results from GraphQL response.
 * @agentic connections
 */
export const getPatientsFromResponse = (
  response: PatientSearchGraphQLResponse,
  query: string,
): Patient[] => {
  const combined: Patient[] = [];
  for (const value of Object.values(response.data)) {
    if (Array.isArray(value)) {
      combined.push(...value);
    }
  }
  return sortPatientsByRelevance(dedupePatients(combined), query).slice(
    0,
    MAX_RESULTS,
  );
};

/**
 * Removes duplicate patients by resource id.
 * @agentic connections
 */
const dedupePatients = (patients: Patient[]): Patient[] => {
  const ids = new Set<string>();
  const result: Patient[] = [];

  for (const patient of patients) {
    if (!patient.id || ids.has(patient.id)) {
      continue;
    }
    ids.add(patient.id);
    result.push(patient);
  }

  return result;
};

/**
 * Sorts patients by best identifier, name, id, or phone telecom match score.
 * @agentic connections
 */
const sortPatientsByRelevance = (
  patients: Patient[],
  query: string,
): Patient[] => {
  const phoneTokens = getPatientMemberListPhoneTelecomValues(query.trim());
  return [...patients].sort(
    (left, right) =>
      getPatientScore(right, query, phoneTokens) -
      getPatientScore(left, query, phoneTokens),
  );
};

/**
 * Computes the best score for a patient match candidate.
 * @agentic connections
 */
const getPatientScore = (
  patient: Patient,
  query: string,
  phoneTokens: string[] | null,
): number => {
  let bestScore = 0;

  for (const identifier of patient.identifier ?? []) {
    bestScore = Math.max(bestScore, getStringScore(identifier.value, query));
  }

  for (const name of patient.name ?? []) {
    bestScore = Math.max(
      bestScore,
      getStringScore(formatHumanName(name), query),
    );
  }

  if (patient.id) {
    bestScore = Math.max(bestScore, getStringScore(patient.id, query));
  }

  if (phoneTokens && phoneTokens.length > 0) {
    const queryDigits = query.replace(/\D/g, "");
    for (const point of patient.telecom ?? []) {
      if (point.system !== "phone" || !point.value) {
        continue;
      }
      const telDigits = point.value.replace(/\D/g, "");
      const strongDigitMatch =
        queryDigits.length >= 7 &&
        telDigits.length >= 7 &&
        (telDigits === queryDigits ||
          (queryDigits.length >= 10 &&
            (telDigits.endsWith(queryDigits.slice(-10)) ||
              queryDigits.endsWith(telDigits.slice(-10)))));
      if (strongDigitMatch) {
        bestScore = Math.max(bestScore, 250);
      }
      for (const token of phoneTokens) {
        bestScore = Math.max(bestScore, getStringScore(point.value, token));
      }
    }
  }

  return bestScore;
};

/**
 * Scores a string by the first index of case-insensitive query match.
 * @agentic connections
 */
const getStringScore = (value: string | undefined, query: string): number => {
  if (!value) {
    return 0;
  }

  const index = value.toLowerCase().indexOf(query.toLowerCase());
  if (index < 0) {
    return 0;
  }

  return 100 - index;
};
